"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberList } from "@/lib/parse-number";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

type InputMode = "manual" | "dataset";

// Abramowitz-Stegun approximation of the error function (formula 7.1.26),
// accurate to about 1.5 × 10⁻⁷.
function erf(x: number): number {
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const t = 1 / (1 + 0.327_591_1 * ax);
  const poly =
    ((((1.061_405_429 * t - 1.453_152_027) * t + 1.421_413_741) * t -
      0.284_496_736) *
      t +
      0.254_829_592) *
    t;
  return sign * (1 - poly * Math.exp(-ax * ax));
}

function standardNormalCdf(z: number): number {
  return 0.5 * (1 + erf(z / Math.SQRT2));
}

function formatNum(value: number, fmt: NumberFormatter): string {
  return fmt(value, { maximumFractionDigits: 4 });
}

interface ZResult {
  x: number;
  mean: number;
  sd: number;
  z: number;
  percentile: number;
  datasetCount: number | null;
}

function buildResult(
  x: number,
  mean: number,
  sd: number,
  datasetCount: number | null
): ZResult | null {
  if (sd <= 0) {
    return null;
  }
  const z = (x - mean) / sd;
  return {
    x,
    mean,
    sd,
    z,
    percentile: standardNormalCdf(z) * 100,
    datasetCount,
  };
}

function computeManual(
  xText: string,
  meanText: string,
  sdText: string
): ZResult | null {
  const x = parseNumberInput(xText);
  const mean = parseNumberInput(meanText);
  const sd = parseNumberInput(sdText);
  if (x === null || mean === null || sd === null) {
    return null;
  }
  return buildResult(x, mean, sd, null);
}

function computeFromDataset(dataText: string, xText: string): ZResult | null {
  const values = parseNumberList(dataText);
  const x = parseNumberInput(xText);
  if (values.length < 2 || x === null) {
    return null;
  }
  const mean = values.reduce((acc, v) => acc + v, 0) / values.length;
  let sumSquares = 0;
  for (const value of values) {
    sumSquares += (value - mean) ** 2;
  }
  const sd = Math.sqrt(sumSquares / values.length);
  return buildResult(x, mean, sd, values.length);
}

const INPUT_CLASS =
  "w-full rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary";

function Field({
  id,
  label,
  value,
  placeholder,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <label className="font-medium text-foreground text-sm" htmlFor={id}>
        {label}
      </label>
      <input
        className={INPUT_CLASS}
        id={id}
        inputMode="decimal"
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type="text"
        value={value}
      />
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const Z_SCORE_CALCULATOR_STRINGS = {
  inputMode: "Input mode",
  fromValueMeanSd: "From value, mean, and SD",
  fromDataset: "From a dataset",
  valueX: "Value (x)",
  meanMu: "Mean (μ)",
  standardDeviationSigma: "Standard deviation (σ)",
  datasetLabel:
    "Your dataset — numbers separated by commas, spaces, or new lines",
  datasetPlaceholder: "62, 68, 71, 74\n70 65 80\n77",
  numbersDetected:
    "one:{count} number detected — mean and population standard deviation are computed from these|other:{count} numbers detected — mean and population standard deviation are computed from these",
  valueToStandardize: "Value to standardize (x)",
  results: "Results",
  zScore: "Z-score",
  percentileApprox: "Percentile (approx.)",
  meanMuLabel: "Mean (μ)",
  computedMeanMu: "Computed mean (μ)",
  stdDeviationSigma: "Std. deviation (σ)",
  populationSdSigma: "Population SD (σ)",
  dataPointsN: "Data points (n)",
  howCalculated: "How this was calculated",
  fromDataPoints: "From your {n} data points: μ = {mean}, population σ = {sd}",
  /*
   * The sentence these three fill was hardcoded English in the JSX, complete with
   * `{Math.abs(result.z) === 1 ? "" : "s"}`.
   *
   * `tool-plurals.test.ts` did not catch it because the noun beside that ternary was a
   * bare literal rather than a widget string, and the guard looks for `strings.<key>`
   * next to the suffix. So the three position words below were translated into sixteen
   * languages and then rendered inside an English sentence — the worst of both, because
   * the page looks translated.
   */
  interpretation:
    "The value {value} sits {deviations} {position}. Assuming a normal distribution, approximately {percentile}% of values fall below it (percentile computed with the Abramowitz-Stegun approximation of the standard normal CDF — treat it as approximate).",
  standardDeviations:
    "one: {count} standard deviation|other: {count} standard deviations",
  aboveTheMean: "above the mean",
  belowTheMean: "below the mean",
  exactlyAtTheMean: "exactly at the mean",
  enterManualPrompt:
    "Enter a value, mean, and a standard deviation greater than zero to see the z-score.",
  enterDatasetPrompt:
    "Paste at least two numbers and enter a value to standardize. The dataset must have a spread greater than zero.",
} as const;

function describePosition(
  z: number,
  strings: typeof Z_SCORE_CALCULATOR_STRINGS
): string {
  if (z > 0) {
    return strings.aboveTheMean;
  }
  if (z < 0) {
    return strings.belowTheMean;
  }
  return strings.exactlyAtTheMean;
}

export default function ZScoreCalculatorWidget() {
  const locale = useToolLocale();
  const strings = useToolUi(Z_SCORE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<InputMode>("manual");
  const [xValue, setXValue] = useState("");
  const [meanValue, setMeanValue] = useState("");
  const [sdValue, setSdValue] = useState("");
  const [dataInput, setDataInput] = useState("");
  const [dataX, setDataX] = useState("");

  const result =
    mode === "manual"
      ? computeManual(xValue, meanValue, sdValue)
      : computeFromDataset(dataInput, dataX);

  const datasetCount = parseNumberList(dataInput).length;

  return (
    <>
      {/* Mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.inputMode}
        </p>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "manual"}
              className="accent-primary"
              name="z-mode"
              onChange={() => setMode("manual")}
              type="radio"
              value="manual"
            />
            {strings.fromValueMeanSd}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "dataset"}
              className="accent-primary"
              name="z-mode"
              onChange={() => setMode("dataset")}
              type="radio"
              value="dataset"
            />
            {strings.fromDataset}
          </label>
        </div>
      </section>

      {/* Inputs */}
      {mode === "manual" ? (
        <section className="grid gap-4 sm:grid-cols-3">
          <Field
            id="z-x"
            label={strings.valueX}
            onChange={setXValue}
            placeholder="85"
            value={xValue}
          />
          <Field
            id="z-mean"
            label={strings.meanMu}
            onChange={setMeanValue}
            placeholder="70"
            value={meanValue}
          />
          <Field
            id="z-sd"
            label={strings.standardDeviationSigma}
            onChange={setSdValue}
            placeholder="10"
            value={sdValue}
          />
        </section>
      ) : (
        <section className="space-y-4">
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="z-data"
            >
              {strings.datasetLabel}
            </label>
            <textarea
              className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id="z-data"
              onChange={(e) => setDataInput(e.target.value)}
              placeholder={strings.datasetPlaceholder}
              value={dataInput}
            />
            <p className="text-muted-foreground text-xs">
              {tPlural(strings.numbersDetected, locale, datasetCount)}
            </p>
          </div>
          <div className="max-w-xs">
            <Field
              id="z-data-x"
              label={strings.valueToStandardize}
              onChange={setDataX}
              placeholder="77"
              value={dataX}
            />
          </div>
        </section>
      )}

      {/* Results */}
      {result && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.results}
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard label={strings.zScore} value={formatNum(result.z, fmt)} />
            <StatCard
              label={strings.percentileApprox}
              value={`${fmt(result.percentile, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}
            />
            <StatCard
              label={
                result.datasetCount === null
                  ? strings.meanMuLabel
                  : strings.computedMeanMu
              }
              value={formatNum(result.mean, fmt)}
            />
            <StatCard
              label={
                result.datasetCount === null
                  ? strings.stdDeviationSigma
                  : strings.populationSdSigma
              }
              value={formatNum(result.sd, fmt)}
            />
            {result.datasetCount !== null && (
              <StatCard
                label={strings.dataPointsN}
                value={result.datasetCount}
              />
            )}
          </div>

          {/* Working */}
          <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
            <h3 className="font-semibold text-foreground text-lg">
              {strings.howCalculated}
            </h3>
            {result.datasetCount !== null && (
              <p className="font-mono text-muted-foreground text-sm leading-relaxed">
                {strings.fromDataPoints
                  .replace("{n}", String(result.datasetCount))
                  .replace("{mean}", formatNum(result.mean, fmt))
                  .replace("{sd}", formatNum(result.sd, fmt))}
              </p>
            )}
            <p className="font-mono text-muted-foreground text-sm leading-relaxed">
              z = (x − μ) / σ = ({formatNum(result.x, fmt)} −{" "}
              {formatNum(result.mean, fmt)}) / {formatNum(result.sd, fmt)} ={" "}
              {formatNum(result.x - result.mean, fmt)} /{" "}
              {formatNum(result.sd, fmt)} ={" "}
              <strong className="text-foreground">
                {formatNum(result.z, fmt)}
              </strong>
            </p>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t(strings.interpretation, {
                value: formatNum(result.x, fmt),
                deviations: tPlural(
                  strings.standardDeviations,
                  locale,
                  Math.abs(result.z),
                  { count: formatNum(Math.abs(result.z), fmt) }
                ).trim(),
                position: describePosition(result.z, strings),
                percentile: fmt(result.percentile, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
              })}
            </p>
          </div>
        </section>
      )}

      {!result && mode === "manual" && (
        <p className="text-muted-foreground text-sm">
          {strings.enterManualPrompt}
        </p>
      )}
      {!result && mode === "dataset" && (
        <p className="text-muted-foreground text-sm">
          {strings.enterDatasetPrompt}
        </p>
      )}
    </>
  );
}
